// Carries an email address or phone number between the sign-in and sign-up
// pages (and in from a link such as /register?email=...) so the user does not
// have to type it twice.
//
// The value is personal data, so it lives in memory only: it is never written
// to storage and never logged. A value that arrives in the address bar is taken
// out of it straight away - production reports the page address, query string
// included, to analytics, and the address also ends up in browser history.
//
// This module deliberately imports nothing: it runs before the rest of the app
// loads (see emailPrefillBootstrap.ts). Callers validate the value on read.

export const EMAIL_PREFILL_PARAM = 'email';

// The longest address an email standard allows; anything longer is not one.
const MAX_PREFILL_LENGTH = 254;

const PREFILL_PATHS: readonly string[] = ['/signin', '/register'];

let pendingPrefill: string | undefined;

// The router matches paths whatever their letter case, so this does too.
const isPrefillPath = (pathname: string): boolean =>
  PREFILL_PATHS.includes(pathname.replace(/\/+$/, '').toLowerCase());

const prefillParamNames = (searchParams: URLSearchParams): string[] =>
  Array.from(new Set(searchParams.keys())).filter(
    (name) => name.toLowerCase() === EMAIL_PREFILL_PARAM,
  );

/** Hold a value for the next sign-in or sign-up form to pick up. */
export const setEmailPrefill = (value: string | undefined): void => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  pendingPrefill = trimmed && trimmed.length <= MAX_PREFILL_LENGTH ? trimmed : undefined;
};

/** The held value, unvalidated, or undefined when there is none. */
export const readEmailPrefill = (): string | undefined => pendingPrefill;

export const clearEmailPrefill = (): void => {
  pendingPrefill = undefined;
};

/**
 * On /signin and /register, move an `email` query parameter out of the address
 * bar and into memory. Every other parameter and the hash are left alone, and
 * so is every other page (the verification link has its own parameters).
 *
 * What this cannot undo: the request that loaded the page already carried the
 * address to our own web server, and so did the Referer of the same-origin
 * files the page loaded before this ran.
 */
export const captureEmailPrefillFromUrl = (): void => {
  try {
    const url = new URL(window.location.href);
    const paramNames = prefillParamNames(url.searchParams);
    if (!isPrefillPath(url.pathname) || !paramNames.length) return;
    setEmailPrefill(url.searchParams.get(paramNames[0]) ?? undefined);
    paramNames.forEach((name) => url.searchParams.delete(name));
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  } catch {
    // A prefill is a convenience: never let it stop the app from starting.
  }
};
